import React, { useContext, useEffect, useRef, useState } from 'react';
import { LoaderCircle, MapPin, Search, X } from 'lucide-react';
import { apiUrl } from '../config/api';
import { UserAuthContext } from '../UserAuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import './MobileLocationPrompt.css';

const DISMISSED_KEY = 'eulua.mobileLocationPromptDismissed';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

// Mobile-only screen: on phones we try IP-based and then device geolocation
// automatically (see UserAuthContext). If both fail, this offers a manual
// address entry instead of silently leaving the location unknown. Skipping
// it changes nothing else - product and seller ordering already fall back
// to the site-wide default whenever no location is known.
const MobileLocationPrompt = () => {
  const {
    loading, location, locationReady, mobileLocationResolved, updateUserLocation,
  } = useContext(UserAuthContext);
  const { t } = useLanguage();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const hasKnownLocation = Boolean(location?.country && location.country !== 'Unknown');
  const shouldShow = !loading && locationReady && mobileLocationResolved && !hasKnownLocation && !dismissed;

  useEffect(() => {
    const trimmed = query.trim();
    if (!shouldShow || trimmed.length < 2) {
      setSuggestions([]);
      setSearching(false);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      setError('');
      try {
        const response = await fetch(apiUrl(`/api/locations/search?q=${encodeURIComponent(trimmed)}`), {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Location search failed');
        setSuggestions(Array.isArray(data.locations) ? data.locations : []);
        if (!data.locations?.length) setError(t('No matching city, state, or country was found.'));
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message || t('Location suggestions are temporarily unavailable.'));
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 450);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, shouldShow, t]);

  if (!shouldShow) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Session storage may be unavailable; the prompt just reappears next visit.
    }
    setDismissed(true);
  };

  const confirm = () => {
    if (!selected) return;
    updateUserLocation(selected);
    dismiss();
  };

  return (
    <div className="mobile-location-prompt" role="dialog" aria-modal="true" aria-labelledby="mobile-location-prompt-title">
      <div className="mobile-location-prompt__card">
        <button type="button" className="mobile-location-prompt__close" onClick={dismiss} aria-label={t('Not now')}>
          <X size={20} />
        </button>
        <MapPin size={32} className="mobile-location-prompt__icon" />
        <h2 id="mobile-location-prompt-title">{t('Set your location')}</h2>
        <p>
          {t("We couldn't detect your location automatically. Enter your address to see accurate delivery estimates, currency, and nearby sellers.")}
        </p>
        <div className="mobile-location-prompt__input-container">
          {searching ? <LoaderCircle size={18} className="mobile-location-prompt__spinner" /> : <Search size={18} />}
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setSelected(null); }}
            placeholder={t('City, state or country')}
            className="mobile-location-prompt__input"
            autoComplete="off"
            aria-label={t('Search for a city, state, or country')}
          />
        </div>
        {suggestions.length > 0 && (
          <div className="mobile-location-prompt__suggestions" role="listbox" aria-label={t('Physical location suggestions')}>
            {suggestions.map((suggestion) => (
              <button
                type="button"
                role="option"
                aria-selected={selected?.id === suggestion.id}
                key={suggestion.id}
                className={`mobile-location-prompt__suggestion ${selected?.id === suggestion.id ? 'is-selected' : ''}`}
                onClick={() => setSelected(suggestion)}
              >
                <MapPin size={15} />
                <span>{suggestion.label}</span>
              </button>
            ))}
          </div>
        )}
        {error && <p className="mobile-location-prompt__error" role="alert">{error}</p>}
        <div className="mobile-location-prompt__actions">
          <button type="button" className="mobile-location-prompt__skip" onClick={dismiss}>
            {t('Not now')}
          </button>
          <button type="button" className="mobile-location-prompt__confirm" onClick={confirm} disabled={!selected}>
            {t('Save location')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileLocationPrompt;
